import { cn } from "@/lib/utils";

function Bone({ className }: { className?: string }) {
  return (
    <div className={cn("animate-pulse rounded bg-brand-600/20", className)} />
  );
}

/** Staggered widths keep the rows from looking like a rigid grid while still filling the row. */
const TASK_ROW_WIDTHS = [
  { title: "w-[46%]", description: "w-[82%]" },
  { title: "w-[38%]", description: "w-[68%]" },
  { title: "w-[54%]", description: "w-[88%]" },
  { title: "w-[42%]", description: "w-[74%]" },
];

function TaskRowStub({
  selected,
  widths,
}: {
  selected?: boolean;
  widths: { title: string; description: string };
}) {
  return (
    <div
      className={cn(
        "flex w-full items-center gap-4 rounded-xl border p-4",
        selected ? "border-brand-600/30 bg-brand-600/5" : "border-border bg-card"
      )}
    >
      <Bone className="size-7 shrink-0 rounded-full" />
      <div className="min-w-0 flex-1 space-y-2 py-0.5">
        <Bone className={cn("h-4", widths.title)} />
        <Bone className={cn("h-3", widths.description)} />
      </div>
      <Bone className="hidden h-8 w-20 shrink-0 rounded-md sm:block" />
      <Bone className="size-4 shrink-0" />
    </div>
  );
}

export function OnboardingSkeleton() {
  return (
    <div
      className="flex-1 pb-16"
      aria-busy="true"
      aria-label="Loading onboarding"
    >
      <div className="mb-6 flex items-center justify-end gap-4">
        <Bone className="hidden h-5 w-24 sm:block" />
        <Bone className="h-8 w-32 rounded-md" />
      </div>

      <div className="mx-auto max-w-3xl">
        <section className="flex flex-col items-center text-center">
          <Bone className="mb-5 h-7 w-36 rounded-full" />
          <Bone className="h-9 w-[70%] max-w-sm sm:h-10 sm:w-[80%] sm:max-w-lg" />
          <Bone className="mt-4 h-4 w-full max-w-xl" />
          <Bone className="mt-4 h-4 w-[70%] max-w-md" />
        </section>

        <div className="mt-10 flex items-center gap-3">
          <Bone className="h-1.5 flex-1 rounded-full" />
          <Bone className="h-4 w-20 shrink-0" />
        </div>

        <section className="mt-5 overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
          <Bone className="aspect-video w-full rounded-none" />
          <div className="space-y-2 border-t border-border px-5 py-4">
            <Bone className="h-4 w-[40%] max-w-xs" />
            <Bone className="h-3 w-[62%] max-w-sm" />
          </div>
        </section>

        <section className="mt-10">
          <div className="mb-4 flex items-end justify-between gap-4">
            <div className="w-full space-y-2">
              <Bone className="h-6 w-40" />
              <Bone className="h-4 w-[45%] max-w-xs" />
            </div>
            <Bone className="hidden h-4 w-40 shrink-0 sm:block" />
          </div>
          <div className="flex flex-col gap-3">
            {TASK_ROW_WIDTHS.map((widths, index) => (
              <TaskRowStub
                key={index}
                selected={index === 0}
                widths={widths}
              />
            ))}
          </div>
        </section>

        <section className="mt-6 flex flex-col gap-4 rounded-2xl bg-muted/60 p-5 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <div className="w-full space-y-2">
            <Bone className="h-5 w-40" />
            <Bone className="h-4 w-[55%] max-w-sm" />
          </div>
          <div className="flex shrink-0 flex-col gap-2 sm:flex-row">
            <Bone className="h-9 w-full rounded-md sm:w-36" />
            <Bone className="h-9 w-full rounded-md sm:w-36" />
          </div>
        </section>
      </div>
    </div>
  );
}

export default OnboardingSkeleton;
